/**
 * useAct1Loop — 第一幕游戏循环 Hook
 *
 * 管理轮次推进、API 调用、消息追加、态度更新。
 * v2: 支持 SSE 流式对话，降级回退到 JSON 模式。
 */
import { useCallback, useRef } from 'react';
import { useAct1Store } from '../stores/act1Store';
import { useRootStore } from '../stores/rootStore';
import { act1Chat, act1Evaluate } from '../services/api/v2GameApi';
import { connectSSE } from '../services/api/sseClient';
import { FlowController } from '../utils/flowController';
import type { Act1Card } from '@shared/types/cards';
import type { RoundSummary } from '@shared/types/agent';
import { ACT1_MAX_ROUNDS, ACT1_REFILL_INTERVAL, FAMILY_ATTITUDE_FAIL } from '@shared/constants/gameBalance';

export function useAct1Loop() {
  const act1 = useAct1Store();
  const root = useRootStore();
  const flowControllerRef = useRef(new FlowController());
  const abortRef = useRef<AbortController | null>(null);

  /** 检查是否有亲友态度低于提前结束阈值 */
  const checkEarlyEnd = useCallback((): boolean => {
    const attitudes: Record<string, number> = useAct1Store.getState().familyAttitudes;
    return Object.values(attitudes).some(v => v <= FAMILY_ATTITUDE_FAIL);
  }, []);

  /** 应用轮次评估结果 */
  const applyMetricChanges = useCallback((summary: RoundSummary) => {
    const store = useAct1Store.getState();
    if (summary.attitudeChanges) {
      for (const [memberId, delta] of Object.entries(summary.attitudeChanges)) {
        store.updateAttitude(memberId, delta);
      }
    }
    if (summary.affectionChangeA) store.adjustAffection('A', summary.affectionChangeA);
    if (summary.affectionChangeB) store.adjustAffection('B', summary.affectionChangeB);
  }, []);

  /** 推进轮次 */
  const advanceRound = useCallback(async () => {
    const store = useAct1Store.getState();

    // Check early end
    if (checkEarlyEnd()) {
      await finishAct1();
      return;
    }

    // Advance round
    const nextRound = store.round + 1;
    store.nextRound();

    // Refill hand at intervals
    if (nextRound % ACT1_REFILL_INTERVAL === 0) {
      store.refillHand();
    }

    // Check if max rounds reached
    if (nextRound > ACT1_MAX_ROUNDS) {
      await finishAct1();
      return;
    }

    store.setPhase('PLAYER_TURN');
  }, [checkEarlyEnd]);

  /** 降级到 JSON 模式 */
  const fallbackToJsonMode = useCallback(async (playedCard?: Act1Card, cardTarget?: string) => {
    const rootState = useRootStore.getState();
    const store = useAct1Store.getState();
    if (!rootState.protagonistA || !rootState.protagonistB) return;

    try {
      const response = await act1Chat({
        protagonistA: rootState.protagonistA,
        protagonistB: rootState.protagonistB,
        familyA: rootState.familyA,
        familyB: rootState.familyB,
        history: store.messages,
        round: store.round,
        familyAttitudes: store.familyAttitudes,
        playedCard,
        cardTarget,
      });

      store.setIsTyping(false);
      store.clearStreaming();

      // Append messages one by one with delay
      for (const msg of response.messages) {
        store.appendMessage(msg);
        await new Promise(r => setTimeout(r, 300));
      }

      // Apply changes
      applyMetricChanges({
        attitudeChanges: response.attitudeChanges,
        affectionChangeA: response.affectionChangeA,
        affectionChangeB: response.affectionChangeB,
      });

      await advanceRound();
    } catch (err) {
      console.error('Act1 fallback also failed:', err);
      store.setIsTyping(false);
      store.clearStreaming();
      store.setPhase('PLAYER_TURN');
    }
  }, [applyMetricChanges, advanceRound]);

  /** 执行一轮对话（SSE 流式模式） */
  const executeRound = useCallback(async (playedCard?: Act1Card, cardTarget?: string) => {
    const rootState = useRootStore.getState();
    if (!rootState.protagonistA || !rootState.protagonistB) return;

    const store = useAct1Store.getState();
    store.setPhase('PROCESSING');
    store.setIsTyping(true);

    // 中断之前的流
    flowControllerRef.current.interrupt();
    if (abortRef.current) {
      abortRef.current.abort();
    }

    const flowToken = flowControllerRef.current.getToken();

    const requestBody = {
      protagonistA: rootState.protagonistA,
      protagonistB: rootState.protagonistB,
      familyA: rootState.familyA,
      familyB: rootState.familyB,
      history: store.messages,
      round: store.round,
      familyAttitudes: store.familyAttitudes,
      playedCard,
      cardTarget,
    };

    const abortCtrl = connectSSE('/api/act1/round', requestBody, {
      onCardNarrativeChunk: (text) => {
        if (!flowToken.isValid()) return;
        const s = useAct1Store.getState();
        s.setIsCardNarrating(true);
        s.appendCardNarrative(text);
      },

      onCardNarrativeEnd: (cardName, _cardType, fullText) => {
        if (!flowToken.isValid()) return;
        const s = useAct1Store.getState();
        s.commitCardNarrative(cardName, fullText);
      },

      onSpeechStart: (agentId, agentName, agentRole) => {
        if (!flowToken.isValid()) return;
        const s = useAct1Store.getState();
        s.setCurrentSpeaker(agentId);
        s.setIsTyping(true);
        s.setTypingSpeaker(agentId);
        s.clearStreaming();
        s.setStreamingAgent(agentName, agentRole);
      },

      onSpeechChunk: (agentId, text) => {
        if (!flowToken.isValid()) return;
        useAct1Store.getState().appendStreamingText(text);
      },

      onSpeechEnd: (_agentId, _fullText) => {
        if (!flowToken.isValid()) return;
        useAct1Store.getState().commitStreamingMessage();
      },

      onRoundComplete: (summary) => {
        if (!flowToken.isValid()) return;
        const s = useAct1Store.getState();
        applyMetricChanges(summary);
        s.setIsTyping(false);
        s.setCurrentSpeaker(null);
        s.clearStreaming();
        advanceRound();
      },

      onSummaryUpdate: (summary) => {
        if (!flowToken.isValid()) return;
        const s = useAct1Store.getState();
        s.setHistorySummary(summary, s.messages.length);
      },

      onError: (err) => {
        console.error('Act1 SSE error, falling back to JSON:', err);
        // 降级到 JSON 模式
        fallbackToJsonMode(playedCard, cardTarget);
      },
    });

    abortRef.current = abortCtrl;
  }, [applyMetricChanges, advanceRound, fallbackToJsonMode]);

  /** 结束第一幕，调用评估 API */
  const finishAct1 = useCallback(async () => {
    const rootState = useRootStore.getState();
    const store = useAct1Store.getState();
    if (!rootState.protagonistA || !rootState.protagonistB) return;

    store.setPhase('FINISHED');

    try {
      const response = await act1Evaluate({
        protagonistA: rootState.protagonistA,
        protagonistB: rootState.protagonistB,
        familyA: rootState.familyA,
        familyB: rootState.familyB,
        history: store.messages,
        familyAttitudes: store.familyAttitudes,
      });

      rootState.finishAct1(response.result);
    } catch (err) {
      console.error('Act1 evaluate failed:', err);
      rootState.finishAct1({
        outcome: '勉强通关',
        familyAttitudes: store.familyAttitudes,
        highlights: ['相亲在混乱中结束'],
        undergroundLove: false,
        comfortPenalty: 0,
        cardEffectReduction: 0,
      });
    }
  }, []);

  /** 玩家出牌 */
  const playCard = useCallback((card: Act1Card, targetId: string) => {
    const removed = useAct1Store.getState().playCard(card.id);
    if (!removed) return;
    executeRound(removed, targetId);
  }, [executeRound]);

  /** 玩家跳过 */
  const skip = useCallback(() => {
    executeRound();
  }, [executeRound]);

  /** 开始第一轮（自动触发开场对话） */
  const startFirstRound = useCallback(() => {
    const store = useAct1Store.getState();
    store.setPhase('PROCESSING');
    store.appendMessage({
      id: `sys_opening_${Date.now()}`,
      senderId: 'system',
      senderName: '系统',
      senderRole: 'system',
      text: '双方家庭落座，相亲正式开始...',
      type: 'system',
    });
    executeRound();
  }, [executeRound]);

  return {
    playCard,
    skip,
    startFirstRound,
    phase: act1.phase,
    round: act1.round,
  };
}
